import { createAction } from "@reduxjs/toolkit";
import {
  INITIATING_SMALL_TALK_CLIENT_REQUEST,
  INITIATING_SMALL_TALK_CLIENT_SUCCEDDED,
  INITIATING_SMALL_TALK_CLIENT_FAILED,
  INITIATE_SMALL_TALK_SESSION,
  INVALIDATE_SMALL_TALK_SESSION,
  TOGGLE_SMALL_TALK_WINDOW_VISIBILITY,
  RECEIVED_SMALL_TALK_MESSAGE,
  SENDING_SMALL_TALK_MESSAGE_REQUEST,
  SENDING_SMALL_TALK_MESSAGE_SUCCEDDED,
  SENDING_SMALL_TALK_MESSAGE_FAILED,
  FETCHING_SMALL_TALK_MESSAGES_REQUEST,
  FETCHING_SMALL_TALK_MESSAGES_SUCCEDDED,
  FETCHING_SMALL_TALK_MESSAGES_FAILED,
  FETCHING_SMALL_TALK_CONTACT_PRESENCE_REQUEST,
  FETCHING_SMALL_TALK_CONTACT_PRESENCE_SUCCEDDED,
  FETCHING_SMALL_TALK_CONTACT_PRESENCE_FAILED,
} from "./actionTypes";
import type { QueryClient } from "@tanstack/react-query";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import axios from "axios";
import { get } from "lodash";
import {
  FETCH,
  SMALL_TALK,
  SMALL_TALK_SESSION,
  SMALL_TALK_MESSAGES,
  SMALL_TALK_PRESENCE,
} from "src/query/queryKeys";

const apiUrl = process.env.REACT_APP_API_URL;
const SMALL_TALK_BASE_URL = `${apiUrl}/api/2.0/small_talk/users/initiate`;

// Minimal type for PubNub-like client used by small talk
export interface SmallTalkClient {
  publish: (message: unknown) => Promise<unknown>;
  fetchMessages: (params: {
    channels: string[];
    count: number;
  }) => Promise<{ channels?: Record<string, unknown[]> }>;
  hereNow: (params: {
    channels: string[];
    includeUUIDs?: boolean;
    includeState?: boolean;
  }) => Promise<{ channels?: Record<string, { occupants?: { uuid?: string }[] }> }>;
}

// ---------------------------------------------------------------------------
// Sync Redux actions (for reducer / UI state)
// ---------------------------------------------------------------------------

export const initiatingSmallTalkRequest = createAction<void>(
  INITIATING_SMALL_TALK_CLIENT_REQUEST
);

export const initiatingSmallTalkSucceeded = createAction<{
  authKey?: string;
  uuid?: string;
  [key: string]: unknown;
}>(INITIATING_SMALL_TALK_CLIENT_SUCCEDDED);

export const initiatingSmallTalkFailed = createAction<unknown>(
  INITIATING_SMALL_TALK_CLIENT_FAILED
);

export const initiateSmallTalkSession = createAction<{
  uuid?: string;
  [key: string]: unknown;
}>(INITIATE_SMALL_TALK_SESSION);

export const invalidateSmallTalkSession = createAction<{
  uuid?: string;
  [key: string]: unknown;
}>(INVALIDATE_SMALL_TALK_SESSION);

export const toggleSmallTalkWindowVisibility = createAction<{
  contact: unknown;
  visible: boolean;
}>(TOGGLE_SMALL_TALK_WINDOW_VISIBILITY);

export const receivedSmallTalkMessage = createAction<{
  channel: string;
  message: unknown;
}>(RECEIVED_SMALL_TALK_MESSAGE);

export const sendingSmallTalkMessageRequest = createAction<unknown>(
  SENDING_SMALL_TALK_MESSAGE_REQUEST
);

export const sendingSmallTalkMessageSucceeded = createAction<{ message: unknown }>(
  SENDING_SMALL_TALK_MESSAGE_SUCCEDDED
);

export const sendingSmallTalkMessageFailed = createAction<{
  message: unknown;
  error: unknown;
}>(SENDING_SMALL_TALK_MESSAGE_FAILED);

export const fetchingSmallTalkMessagesRequest = createAction<string>(
  FETCHING_SMALL_TALK_MESSAGES_REQUEST
);

export const fetchingSmallTalkMessagesSucceeded = createAction<{
  channel: string;
  response: unknown[];
}>(FETCHING_SMALL_TALK_MESSAGES_SUCCEDDED);

export const fetchingSmallTalkMessagesFailed = createAction<{
  channel: string;
  error: unknown;
}>(FETCHING_SMALL_TALK_MESSAGES_FAILED);

export const fetchingSmallTalkContactPresenceRequest = createAction<{
  uuid: string | null;
  contacts: string[];
}>(FETCHING_SMALL_TALK_CONTACT_PRESENCE_REQUEST);

export const fetchingSmallTalkContactPresenceSucceeded = createAction<{
  uuid: string | null;
  contacts: string[];
  response: Record<string, boolean>;
}>(FETCHING_SMALL_TALK_CONTACT_PRESENCE_SUCCEDDED);

export const fetchingSmallTalkContactPresenceFailed = createAction<{
  uuid: string | null;
  contacts: string[];
  error: unknown;
}>(FETCHING_SMALL_TALK_CONTACT_PRESENCE_FAILED);

/** Dispatches presence update for a single contact (same shape as FETCHING_*_SUCCEDDED for reducer) */
export function togglePresenceForContact(contact: string, available: boolean) {
  return fetchingSmallTalkContactPresenceSucceeded({
    uuid: null,
    contacts: [contact],
    response: { [contact]: available },
  });
}

// ---------------------------------------------------------------------------
// API (HTTP)
// ---------------------------------------------------------------------------

export async function initiateSmallTalkApi(): Promise<{
  authKey?: string;
  uuid?: string;
  [key: string]: unknown;
}> {
  const { data } = await axios.post(SMALL_TALK_BASE_URL);
  return data;
}

// ---------------------------------------------------------------------------
// Query key factories
// ---------------------------------------------------------------------------

export const smallTalkKeys = {
  all: [SMALL_TALK] as const,
  session: () => [SMALL_TALK, SMALL_TALK_SESSION] as const,
  initiate: () => [SMALL_TALK, "initiate"] as const,
  messages: (channel: string | string[]) => [SMALL_TALK_MESSAGES, FETCH, Array.isArray(channel) ? channel[0] : channel] as const,
  presence: (uuid: string | null, contacts: string[]) =>
    [SMALL_TALK_PRESENCE, FETCH, uuid, contacts] as const,
};

export type SmallTalkSession = { authKey?: string; uuid?: string };

/**
 * Writes small talk session (authKey, uuid) to React Query cache. Call when initiate succeeds.
 * Keeps React Query as single source of truth for session.
 */
export function setSmallTalkSessionCache(
  queryClient: QueryClient,
  data: SmallTalkSession
): void {
  queryClient.setQueryData<SmallTalkSession>(smallTalkKeys.session(), (prev) => ({
    ...(prev ?? {}),
    ...data,
  }));
}

const EMPTY_SESSION: SmallTalkSession = { authKey: "", uuid: "" };

/**
 * Returns the current small talk uuid from React Query cache (single source of truth).
 * Populate cache via setSmallTalkSessionCache when initiate succeeds.
 */
export function useSmallTalkUuid(): string {
  const { data } = useQuery({
    queryKey: smallTalkKeys.session(),
    queryFn: () => EMPTY_SESSION,
    initialData: EMPTY_SESSION,
    staleTime: Number.POSITIVE_INFINITY,
  });
  return (data?.uuid as string) ?? "";
}

// ---------------------------------------------------------------------------
// React Query hooks
// ---------------------------------------------------------------------------

export function useInitiateSmallTalkMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: initiateSmallTalkApi,
    onSuccess: (data) => {
      if (data?.authKey != null || data?.uuid != null) {
        setSmallTalkSessionCache(queryClient, data as SmallTalkSession);
      }
      queryClient.invalidateQueries({ queryKey: smallTalkKeys.all });
    },
  });
}

export function useSendSmallTalkMessageMutation() {
  return useMutation({
    mutationFn: async (params: {
      client: SmallTalkClient;
      message: unknown;
    }) => {
      const { client, message } = params;
      return client.publish(message);
    },
  });
}

export function useSmallTalkMessagesQuery(params: {
  client: SmallTalkClient | null;
  channel: string | null;
  limit: number;
}) {
  const { client, channel, limit } = params;
  return useQuery({
    queryKey: smallTalkKeys.messages(channel ?? ""),
    queryFn: async () => {
      if (!client || !channel) return [];
      const response = await client.fetchMessages({
        channels: Array.isArray(channel) ? channel : [channel],
        count: limit,
      });
      const messages = (response?.channels?.[Array.isArray(channel) ? channel[0] : channel] ?? []) as unknown[];
      return messages;
    },
    enabled: Boolean(client && channel),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
}

export function useSmallTalkPresenceQuery(params: {
  client: SmallTalkClient | null;
  uuid: string | null;
  contacts: string[];
}) {
  const { client, uuid, contacts } = params;
  return useQuery({
    queryKey: smallTalkKeys.presence(uuid, contacts),
    queryFn: async () => {
      if (!client || !uuid || contacts.length === 0) return {};
      const channels = contacts.map((id) => `${uuid}.c_${id}`);
      const response = await client.hereNow({
        channels,
        includeUUIDs: true,
        includeState: true,
      });
      const result: Record<string, boolean> = {};
      contacts.forEach((contact) => {
        const channelName = `${uuid}.c_${contact}`;
        const occupants = get(response, `channels['${channelName}'].occupants`, []) as { uuid?: string }[];
        result[contact] = (occupants?.[0]?.uuid ?? null) != null;
      });
      return result;
    },
    enabled: Boolean(client && uuid && contacts.length > 0),
  });
}

export function appendReceivedMessageToCache(
  queryClient: QueryClient,
  channel: string | string[],
  message: unknown,
  timetoken: string | number
): void {

  const normalizedChannel = Array.isArray(channel) ? channel[0] : channel;
  const queryKey = smallTalkKeys.messages(normalizedChannel);

  queryClient.setQueryData<unknown[]>(queryKey, (prev) => [
    ...(Array.isArray(prev) ? prev : []),
    { message, timetoken },
  ]);
}



export const smallTalkApi = {
  initiate: initiateSmallTalkApi,
};
